import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Button, 
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import clientsApi from '../../api/clientsApi';
import PaginatedTable from '../../components/PaginatedTable';
import SortableTableHeader from '../../components/SortableTableHeader';
import { formatFormCategory, formatFormName } from '../../utils/displayNames';

const ClientForms = ({ clientId, client }) => {
  const navigate = useNavigate();
  // Client data is passed as prop, no need for state
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalForms, setTotalForms] = useState(0);
  const [sortField, setSortField] = useState('submitted_date');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch forms data for this client using the client-specific endpoint
      const formsData = await clientsApi.getClientForms(clientId, {
        skip: page * pageSize,
        limit: pageSize,
        order_by: sortField,
        order_direction: sortDirection
      });
      const baseForms = formsData.forms || [];
      setForms(baseForms);
      setTotalForms(formsData.total || (baseForms ? baseForms.length : 0));
      setError(null);

      // If some rows are missing names/categories, try to hydrate them from the submission detail endpoint
      const needsHydration = baseForms.some(f => !(f?.form_name || f?.name || f?.title));
      if (needsHydration) {
        const hydrated = await Promise.all(
          baseForms.map(async (item) => {
            if (item?.form_name || item?.name || item?.title) return item;
            const submissionId = item?.id || item?.submission_id || item?.form_submission_id;
            if (!submissionId) return item;
            try {
              const detail = await clientsApi.getFormSubmission(submissionId);
              return {
                ...item,
                form_name: detail?.form_name || detail?.name || detail?.title || item?.form_name,
                form_category: detail?.form_category || detail?.category || detail?.type || item?.form_category,
                status: item?.status || detail?.status,
                submitted_date: item?.submitted_date || detail?.submitted_date || detail?.pabau_created_at,
              };
            } catch (e) {
              return item;
            }
          })
        );
        setForms(hydrated);
      }
    } catch (err) {
      console.error('Error fetching client forms:', err);
      setError('Failed to load client forms. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, page, pageSize, sortField, sortDirection]);

  const handleBack = () => {
    // This component is embedded in the client detail page, so we don't need navigation
    // The navigation is handled by the parent component
  };

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleViewForm = async (form) => {
    const submissionId = form?.id || form?.submission_id || form?.form_submission_id;
    if (submissionId) {
      try {
        setModalLoading(true);
        const detail = await clientsApi.getFormSubmission(submissionId);
        setSelectedForm({ ...form, ...detail });
        setModalOpen(true);
      } catch (e) {
        // If detail fetch fails, show whatever we have
        setSelectedForm(form);
        setModalOpen(true);
      } finally {
        setModalLoading(false);
      }
      return;
    }
    // No id present; open modal with available data
    setSelectedForm(form);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedForm(null);
    setModalOpen(false);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'questionnaire':
        return 'primary';
      case 'consent':
        return 'secondary';
      case 'treatment':
        return 'info';
      case 'lab':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getFormName = (form) => formatFormName(form);

  const getFormCategory = (form) => formatFormCategory(form);

  const renderResponses = (form) => {
    if (!form) return null;
    let data = form.form_data || form.submission_data;
    if (!data) return null;

    // Try to parse JSON strings
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{data}</Typography>
        );
      }
    }

    // If standard answers array
    if (data && typeof data === 'object' && Array.isArray(data.answers)) {
      return (
        <Paper 
          variant="outlined" 
          sx={{ p: 2, backgroundColor: 'grey.50', maxHeight: '400px', overflow: 'auto' }}
        >
          {data.answers.map((answer, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              {answer.question && (
                <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
                  {answer.question}
                </Typography>
              )}
              {answer.answer && (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {String(answer.answer)}
                </Typography>
              )}
              {Array.isArray(answer.files) && answer.files.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Files: {answer.files.length} attached
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Paper>
      );
    }

    // Fallback generic object / string
    if (typeof data === 'object') {
      return (
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(data, null, 2)}
          </Typography>
        </Paper>
      );
    }

    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{String(data)}</Typography>
    );
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size
  };

  return (
    <Box>

      <PaginatedTable
        title={`Forms for ${client?.first_name} ${client?.last_name}`}
        icon={<DescriptionIcon color="primary" sx={{ mr: 1 }} />}
        total={totalForms}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
      >
        {forms.length === 0 ? (
          <Alert severity="info">No form submissions found for this client.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableHeader
                    field="form_name"
                    label="Form Name"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="submitted_date"
                    label="Submitted Date"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="status"
                    label="Status"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.map((form) => (
                  <TableRow key={form.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {getFormName(form)}
                        </Typography>
                        <Chip 
                          label={getFormCategory(form)} 
                          size="small" 
                          color={getCategoryColor(getFormCategory(form))}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{formatDateTime(form.submitted_date)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={form.status || 'Unknown'} 
                        color={getStatusColor(form.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Form Details">
                        <IconButton 
                          onClick={() => handleViewForm(form)}
                          size="small"
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PaginatedTable>

      {/* Form Details Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{selectedForm ? getFormName(selectedForm) : 'Form Submission Details'}</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {modalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : selectedForm && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {getFormName(selectedForm)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {getFormCategory(selectedForm) && (
                    <Chip 
                      label={getFormCategory(selectedForm)} 
                      color={getCategoryColor(getFormCategory(selectedForm))}
                      size="small"
                    />
                  )}
                  <Chip 
                    label={selectedForm.status || 'Unknown'} 
                    color={getStatusColor(selectedForm.status)}
                    size="small"
                  />
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Form Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Form Name:</Typography>
                    <Typography variant="body1">{getFormName(selectedForm)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Category:</Typography>
                    <Typography variant="body1">{getFormCategory(selectedForm) || 'Not specified'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Typography variant="body1">{selectedForm.status || 'Unknown'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Submitted Date:</Typography>
                    <Typography variant="body1">{formatDateTime(selectedForm.submitted_date)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Created Date:</Typography>
                    <Typography variant="body1">{formatDateTime(selectedForm.created_date)}</Typography>
                  </Box>
                </Box>
              </Box>
              
              <>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Form Responses
                  </Typography>
                  {renderResponses(selectedForm)}
                </Box>
              </>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientForms;
